import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useStudioWorkspace } from "../../firestoreStudio/context/StudioWorkspaceProvider";
import { useStudioRuntime } from "../../firestoreStudio/runtime/StudioRuntimeProvider";
import { checkRuntimeWorkspaceAlignment } from "../../firestoreStudio/runtime/runtimeDb";
import { FA_FAVORITES_KEY, FA_RECENT_KEY, PAGE_LIMIT_OPTIONS } from "../constants";
import { DISCOVERY_SOURCE } from "../constants/discoverySources";
import { resolveAdminRole, roleLabel, hasPermission, Permission } from "../access";
import { fetchDocuments, getCollectionCount } from "../services/firestoreDataService";
import { todayStats } from "../services/auditLogService";
import { trackRead } from "../services/monitorService";
import { estimateBytes } from "../utils/firestoreValueUtils";
import {
  listRegisteredCollections,
  registerExistingCollection,
  rediscoverCollections as rediscoverCollectionsFromRuntime,
  syncRegistryFromServer,
} from "../../../services/collectionRegistryService";

const FirestoreAdminContext = createContext(null);

function loadJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function filterToRegistered(names, registered) {
  const valid = new Set(registered.map((c) => c.collectionName));
  return names.filter((n) => valid.has(n));
}

function formatDocumentLoadError(err) {
  const msg = String(err?.message || "");
  if (err?.code === "permission-denied" || /missing or insufficient permissions/i.test(msg)) {
    return (
      "Cannot read this collection from the browser — security rules blocked access. " +
      "Discovery lists collection names via Admin SDK, but document reads require customer auth " +
      "or rules that allow your session."
    );
  }
  return msg || "Failed to load documents";
}

function pickCollectionName(prev, list, runtimeReady) {
  if (!runtimeReady) return "";
  if (prev && list.some((c) => c.collectionName === prev)) return prev;
  return list[0]?.collectionName || "";
}

export function FirestoreAdminProvider({ children }) {
  const { user } = useAuth();
  const { workspace } = useStudioWorkspace();
  const {
    isRuntimeActive,
    runtimeStatus,
    loading: runtimeLoading,
    runtimeConnection,
    runtimeVersion,
    runtimeConnectionId,
    runtimeProjectId,
    reconnect,
  } = useStudioRuntime();
  const adminRole = resolveAdminRole(user);

  const [registeredCollections, setRegisteredCollections] = useState([]);
  const [registryLoading, setRegistryLoading] = useState(true);
  const [registrySyncing, setRegistrySyncing] = useState(false);
  const [registrySource, setRegistrySource] = useState(null);
  const [registryError, setRegistryError] = useState("");
  const [documentLoadError, setDocumentLoadError] = useState("");
  const [registryDiagnostics, setRegistryDiagnostics] = useState(null);
  const [discoverySource, setDiscoverySource] = useState(null);
  const [collectionsDiscovered, setCollectionsDiscovered] = useState(0);
  const [collectionsLoaded, setCollectionsLoaded] = useState(0);
  const [registryStatus, setRegistryStatus] = useState("");
  const [lastDataRefresh, setLastDataRefresh] = useState(null);
  const [collectionName, setCollectionName] = useState("");
  const [favorites, setFavorites] = useState(() => loadJson(FA_FAVORITES_KEY, []));
  const [recent, setRecent] = useState(() => loadJson(FA_RECENT_KEY, []));
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [sortField, setSortField] = useState("id");
  const [sortAsc, setSortAsc] = useState(true);
  const [recordLimit, setRecordLimit] = useState(PAGE_LIMIT_OPTIONS[1]);
  const [docIdQuery, setDocIdQuery] = useState("");
  const [fieldQuery, setFieldQuery] = useState("");
  const [dashboardStats, setDashboardStats] = useState({
    collections: 0,
    documents: 0,
    todayUpdates: 0,
    todayDeletes: 0,
    storageBytes: 0,
  });

  const expectedFirebaseProjectId =
    runtimeConnection?.firebaseProjectId || runtimeConnection?.projectId || null;

  const runtimeAlignment = useMemo(
    () =>
      checkRuntimeWorkspaceAlignment({
        workspaceConnectionId: workspace.connectionId,
        runtimeConnectionId: runtimeConnectionId || null,
        runtimeProjectId: runtimeProjectId || null,
        expectedFirebaseProjectId,
      }),
    [
      workspace.connectionId,
      runtimeConnectionId,
      runtimeProjectId,
      expectedFirebaseProjectId,
    ]
  );

  const runtimeMismatch = Boolean(
    workspace.connectionId &&
    isRuntimeActive &&
    !runtimeAlignment.ok &&
    (runtimeAlignment.reason === "connection_mismatch" || runtimeAlignment.reason === "project_mismatch")
  );

  const runtimeReady = Boolean(
    workspace.connectionId && isRuntimeActive && !runtimeMismatch && !runtimeLoading
  );

  const allCollections = useMemo(
    () => registeredCollections.map((c) => c.collectionName),
    [registeredCollections]
  );

  const collectionMeta = useMemo(() => {
    const map = new Map();
    registeredCollections.forEach((c) => map.set(c.collectionName, c));
    return map;
  }, [registeredCollections]);

  const applyCollectionList = useCallback(
    (list, source, diagnostics = null) => {
      setRegisteredCollections(list);
      setCollectionsLoaded(list.length);
      setCollectionsDiscovered(diagnostics?.collectionsDiscovered ?? list.length);
      setDiscoverySource(source);
      setRegistrySource(source);
      setLastDataRefresh(new Date().toISOString());
      setRegistryStatus(list.length ? `Cache synced · ${list.length} collections` : "Empty");
      if (diagnostics) setRegistryDiagnostics(diagnostics);
      setFavorites((prev) => {
        const next = filterToRegistered(prev, list);
        if (next.length !== prev.length) localStorage.setItem(FA_FAVORITES_KEY, JSON.stringify(next));
        return next;
      });
      setRecent((prev) => {
        const next = filterToRegistered(prev, list);
        if (next.length !== prev.length) localStorage.setItem(FA_RECENT_KEY, JSON.stringify(next));
        return next;
      });
      setCollectionName((prev) => pickCollectionName(prev, list, runtimeReady));
    },
    [runtimeReady]
  );

  const loadCollectionsFromRuntime = useCallback(
    async (source = DISCOVERY_SOURCE.RUNTIME_DISCOVERY) => {
      if (!runtimeReady) {
        setRegisteredCollections([]);
        setCollectionName("");
        return [];
      }
      setRegistryLoading(true);
      setRegistryError("");
      try {
        const list = await listRegisteredCollections({
          createdBy: user?.uid || user?.email || null,
          discoverySource: source,
        });
        const diagnostics = {
          discoverySource: source,
          collectionsDiscovered: list.length,
          collectionsRendered: list.length,
          registryRead: list.length,
          runtimeProjectId: runtimeProjectId || null,
          collectionsReturned: list.map((c) => c.collectionName),
        };
        applyCollectionList(list, source, diagnostics);
        console.info("[FirestoreAdmin] Collections Rendered", diagnostics);
        return list;
      } catch (err) {
        const message = err?.message || "Failed to discover collections from runtime";
        console.error("[FirestoreAdmin] loadCollectionsFromRuntime failed", err);
        setRegistryError(message);
        setRegisteredCollections([]);
        setCollectionName("");
        return [];
      } finally {
        setRegistryLoading(false);
      }
    },
    [applyCollectionList, runtimeProjectId, runtimeReady, user?.email, user?.uid]
  );

  const rediscoverCollections = useCallback(async () => {
    if (!runtimeReady) {
      setRegistryError("No active workspace connection.");
      return null;
    }
    setRegistrySyncing(true);
    setRegistryError("");
    try {
      const result = await rediscoverCollectionsFromRuntime(user?.uid || user?.email || null);
      const diagnostics = {
        discoverySource: result.discoverySource,
        collectionsDiscovered: result.collectionsDiscovered,
        collectionsRendered: result.registeredCollections.length,
        registryWritten: result.registeredCollections.length,
        registryRead: result.registeredCollections.length,
        runtimeProjectId: result.runtimeProjectId,
        collectionsReturned: result.registeredCollections.map((c) => c.collectionName),
        registryUpdated: result.registryUpdated,
      };
      applyCollectionList(result.registeredCollections, result.discoverySource, diagnostics);
      setRows([]);
      setSelected(new Set());
      setCursor(null);
      console.info("[FirestoreAdmin] Registry Updated (rediscover)", diagnostics);
      return result;
    } catch (err) {
      const message = err?.message || "Rediscover failed";
      setRegistryError(message);
      setRegisteredCollections([]);
      setCollectionName("");
      throw err;
    } finally {
      setRegistrySyncing(false);
    }
  }, [applyCollectionList, runtimeReady, user?.email, user?.uid]);

  const syncRegistry = useCallback(async () => {
    if (!runtimeReady) return null;
    setRegistrySyncing(true);
    setRegistryError("");
    try {
      const result = await syncRegistryFromServer(user?.uid || user?.email || null, {
        discoverySource: DISCOVERY_SOURCE.BACKGROUND_REFRESH,
      });
      const list = result.registeredCollections || [];
      const diagnostics = {
        ...result.pipeline,
        discoverySource: result.discoverySource || DISCOVERY_SOURCE.BACKGROUND_REFRESH,
        collectionsDiscovered: result.collectionsDiscovered ?? list.length,
        collectionsRendered: list.length,
        collectionsReturned: result.discovered || list.map((c) => c.collectionName),
        runtimeProjectId: result.runtimeProjectId,
      };
      applyCollectionList(list, result.discoverySource || DISCOVERY_SOURCE.BACKGROUND_REFRESH, diagnostics);
      return result;
    } catch (err) {
      const message = err?.message || "Collection discovery failed";
      setRegistryError(message);
      setRegisteredCollections([]);
      setCollectionName("");
      throw err;
    } finally {
      setRegistrySyncing(false);
    }
  }, [applyCollectionList, runtimeReady, user?.email, user?.uid]);

  const registerCollectionByName = useCallback(
    async (name) => {
      if (!runtimeReady) throw new Error("No active workspace connection.");
      await registerExistingCollection(name, user?.uid || user?.email || null);
      await loadCollectionsFromRuntime(DISCOVERY_SOURCE.MANUAL_REFRESH);
    },
    [loadCollectionsFromRuntime, runtimeReady, user?.email, user?.uid]
  );

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!workspace.connectionId) {
        setRegistryLoading(false);
        setRegisteredCollections([]);
        setCollectionName("");
        setRegistryError("");
        setDiscoverySource(null);
        return;
      }
      if (runtimeLoading) return;
      if (runtimeStatus === "error") {
        setRegistryError("Connection runtime failed to initialize. Re-select the connection in the workspace bar.");
        setRegistryLoading(false);
        return;
      }
      if (!isRuntimeActive) return;
      if (runtimeMismatch) {
        setRegistryError("Runtime mismatch detected.");
        setRegistryLoading(false);
        setRegisteredCollections([]);
        setCollectionName("");
        return;
      }

      console.info("[FirestoreAdmin] bootstrap collections from runtime", {
        connectionId: workspace.connectionId,
        runtimeProjectId,
        runtimeVersion,
      });

      if (!cancelled) {
        await loadCollectionsFromRuntime(DISCOVERY_SOURCE.RUNTIME_DISCOVERY);
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, [
    workspace.connectionId,
    isRuntimeActive,
    runtimeStatus,
    runtimeLoading,
    runtimeMismatch,
    runtimeProjectId,
    runtimeVersion,
    loadCollectionsFromRuntime,
  ]);

  const selectCollection = useCallback(
    (name) => {
      const trimmed = name.trim();
      if (!trimmed || !allCollections.includes(trimmed)) return;
      setCollectionName(trimmed);
      setRecent((prev) => {
        const next = [trimmed, ...prev.filter((c) => c !== trimmed)].slice(0, 8);
        localStorage.setItem(FA_RECENT_KEY, JSON.stringify(next));
        return next;
      });
    },
    [allCollections]
  );

  const toggleFavorite = useCallback((name) => {
    if (!allCollections.includes(name)) return;
    setFavorites((prev) => {
      const next = prev.includes(name) ? prev.filter((c) => c !== name) : [name, ...prev];
      localStorage.setItem(FA_FAVORITES_KEY, JSON.stringify(next));
      return next;
    });
  }, [allCollections]);

  const loadDocuments = useCallback(
    async ({ append = false } = {}) => {
      if (!collectionName || !runtimeReady) return;
      setLoading(true);
      setDocumentLoadError("");
      try {
        const result = await fetchDocuments({
          collectionName,
          sortField,
          sortAsc,
          pageLimit: recordLimit,
          cursor: append ? cursor : null,
          docIdQuery,
          fieldQuery,
          actorId: user?.uid || user?.email || null,
        });
        trackRead(result.rows.length);
        setHasMore(result.hasMore);
        setCursor(result.cursor);
        setRows((prev) => (append ? [...prev, ...result.rows] : result.rows));
        if (!append) setSelected(new Set());
        setLastDataRefresh(new Date().toISOString());
        try {
          const count = await getCollectionCount(collectionName);
          setTotalCount(count);
          trackRead(1);
        } catch {
          setTotalCount(append ? rows.length + result.rows.length : result.rows.length);
        }
      } catch (err) {
        console.error("[FirestoreAdmin] loadDocuments failed", err);
        setDocumentLoadError(formatDocumentLoadError(err));
        if (!append) {
          setRows([]);
          setSelected(new Set());
          setHasMore(false);
          setCursor(null);
          setTotalCount(0);
        }
      } finally {
        setLoading(false);
      }
    },
    [collectionName, runtimeReady, sortField, sortAsc, recordLimit, cursor, docIdQuery, fieldQuery, rows.length, user?.email, user?.uid]
  );

  const refreshDashboard = useCallback(async () => {
    if (!runtimeReady) return;
    setLoading(true);
    try {
      let documents = 0;
      for (const entry of registeredCollections.slice(0, 12)) {
        try {
          if (typeof entry.documentCount === "number") {
            documents += entry.documentCount;
          } else {
            documents += await getCollectionCount(entry.collectionName);
          }
        } catch {
          /* skip */
        }
      }
      const stats = await todayStats(user?.uid);
      const sample = rows.length ? rows : [];
      setDashboardStats({
        collections: registeredCollections.length,
        documents,
        todayUpdates: stats.updates,
        todayDeletes: stats.deletes,
        storageBytes: estimateBytes(sample),
      });
    } finally {
      setLoading(false);
    }
  }, [runtimeReady, registeredCollections, rows, user?.uid]);

  useEffect(() => {
    setRows([]);
    setSelected(new Set());
    setCursor(null);
    setCollectionName("");
  }, [workspace.connectionId, runtimeVersion]);

  useEffect(() => {
    if (!collectionName || !runtimeReady) return;
    loadDocuments();
  }, [collectionName, sortField, sortAsc, recordLimit, runtimeReady, runtimeVersion, loadDocuments]);

  const value = {
    user,
    adminRole,
    roleLabel: roleLabel(adminRole),
    can: (perm) => hasPermission(user, perm),
    Permission,
    registeredCollections,
    collectionMeta,
    registryLoading,
    registrySyncing,
    registrySource,
    registryError,
    documentLoadError,
    registryDiagnostics,
    discoverySource,
    collectionsDiscovered,
    collectionsLoaded,
    registryStatus,
    lastDataRefresh,
    runtimeMismatch,
    runtimeReady,
    loadRegistry: loadCollectionsFromRuntime,
    syncRegistry,
    rediscoverCollections,
    registerCollectionByName,
    collectionName,
    selectCollection,
    favorites,
    recent,
    toggleFavorite,
    allCollections,
    rows,
    setRows,
    selected,
    setSelected,
    loading,
    setLoading,
    totalCount,
    hasMore,
    cursor,
    sortField,
    setSortField,
    sortAsc,
    setSortAsc,
    recordLimit,
    setRecordLimit,
    docIdQuery,
    setDocIdQuery,
    fieldQuery,
    setFieldQuery,
    loadDocuments,
    refreshDashboard,
    dashboardStats,
    reconnectRuntime: reconnect,
  };

  return (
    <FirestoreAdminContext.Provider value={value}>{children}</FirestoreAdminContext.Provider>
  );
}

export function useFirestoreAdmin() {
  const ctx = useContext(FirestoreAdminContext);
  if (!ctx) throw new Error("useFirestoreAdmin must be used within FirestoreAdminProvider");
  return ctx;
}
